import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatDkk } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, AlertTriangle, Star, Check, Play, Square, PhoneOff } from "lucide-react";
import BookingTimeline from "@/components/BookingTimeline";
import VideoPrejoinPanel from "@/components/video/VideoPrejoinPanel";

export default function ConsultationRoom() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [problem, setProblem] = useState(null);
  const [expert, setExpert] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [review, setReview] = useState({ rating: 5, expertise: 5, helpfulness: 5, communication: 5, comment: "" });
  const chatRef = useRef(null);
  const lastMsgCountRef = useRef(0);
  const nav = useNavigate();

  useEffect(() => {
    const load = async () => {
      const { data: b } = await api.get(`/bookings/${bookingId}`);
      setBooking(b);
      setReviewed(Boolean(b.has_review));
      if (b.problem_id) api.get(`/problems/${b.problem_id}`).then((r) => setProblem(r.data)).catch(() => {});
      api.get(`/experts/${b.expert_id}`).then((r) => setExpert(r.data));
      api.get(`/messages/${bookingId}`).then((r) => setMessages(r.data));
      setStarted(!!b.started_at);
      const isEnded = b.status === "completed";
      setEnded(isEnded);
      if (isEnded && typeof b.elapsed_seconds === "number") {
        setElapsed(b.elapsed_seconds);
      } else if (b.started_at && !isEnded) {
        const startMs = new Date(b.started_at).getTime();
        setElapsed(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
      }
    };
    load();
    const poll = setInterval(() => {
      api.get(`/messages/${bookingId}`).then((r) => setMessages(r.data)).catch(() => {});
    }, 4000);
    return () => clearInterval(poll);
  }, [bookingId]);

  useEffect(() => {
    if (!started || ended) return;
    const t = setInterval(() => setElapsed((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [started, ended]);

  useEffect(() => {
    if (messages.length > lastMsgCountRef.current) {
      const el = chatRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
    lastMsgCountRef.current = messages.length;
  }, [messages.length]);

  if (!booking) return <div className="max-w-6xl mx-auto p-10 text-brand-muted">Indlæser…</div>;

  const send = async () => {
    if (!text.trim()) return;
    const { data } = await api.post("/messages", { booking_id: bookingId, content: text });
    setMessages((m) => [...m, data]);
    setText("");
  };

  const startConsult = async () => {
    await api.post(`/bookings/${bookingId}/status`, { status: "started" });
    setStarted(true);
    setElapsed(0);
  };

  const endConsult = async () => {
    await api.post(`/bookings/${bookingId}/status`, { status: "completed", elapsed_seconds: elapsed });
    setEnded(true);
    if (user?.role === "customer") setShowReview(true);
  };

  const flagUnsafe = async () => {
    await api.post(`/bookings/${bookingId}/status`, { status: "unsafe_ended", elapsed_seconds: elapsed });
    setEnded(true);
  };

  const submitReview = async () => {
    if (reviewed || reviewBusy) return;
    setReviewBusy(true);
    setReviewError("");
    try {
      await api.post("/reviews", { booking_id: bookingId, ...review });
      setReviewed(true);
      setShowReview(false);
      nav("/dashboard");
    } catch (requestError) {
      setReviewError(requestError.response?.data?.detail || "Anmeldelsen kunne ikke gemmes.");
    } finally {
      setReviewBusy(false);
    }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const remaining = booking.duration_min * 60 - elapsed;
  const rmm = Math.max(0, Math.floor(remaining / 60));
  const chatStatus = ended ? "Afsluttet" : started ? "Live" : "Venter på start";
  const chatDot = ended ? "bg-brand-muted" : started ? "bg-[#166534]" : "bg-brand-fresh";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6" data-testid="consultation-room-container">
      <BookingTimeline booking={booking}/>
      <div className="mt-4 grid lg:grid-cols-[320px_1fr] gap-4 lg:gap-6">
        {/* Sidebar */}
        <aside className="card-outline p-4 sm:p-5 h-fit lg:sticky lg:top-20">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${chatDot}`}/>
            <span className="text-xs font-mono uppercase tracking-widest text-brand-muted" data-testid="consult-status">{chatStatus}</span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-display text-3xl font-bold text-brand-ink" data-testid="consult-timer">{mm}:{ss}</span>
            <span className="text-xs text-brand-muted">/ {booking.duration_min} min</span>
          </div>
          <div className={`text-xs mt-0.5 ${!ended && remaining < 60 ? "text-[#991B1B]" : "text-brand-muted"}`}>
            {ended ? "Varighed" : started ? `${rmm} min tilbage` : "Ikke startet"}
          </div>

          {expert && (
            <div className="mt-4 pt-4 border-t border-brand-soft flex items-center gap-3">
              <img src={expert.photo_url} className="w-10 h-10 rounded-full object-cover" alt=""/>
              <div className="min-w-0">
                <div className="text-sm font-medium text-brand-ink truncate">{expert.name}</div>
                <div className="text-xs text-brand-muted truncate">{expert.headline}</div>
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-brand-soft">
            <div className="font-mono uppercase text-[10px] tracking-widest text-brand-muted">Problem</div>
            <div className="mt-1 font-medium text-brand-ink text-sm">{problem?.title || booking.problem_title || booking.customer_name}</div>
            {problem?.description && <p className="mt-1 text-xs text-brand-body leading-relaxed line-clamp-4">{problem.description}</p>}
            {problem?.media?.length > 0 && (
              <div className="mt-2 grid grid-cols-3 gap-1">
                {problem.media.filter((m) => m.type?.startsWith("image")).slice(0, 3).map((m, i) => (
                  <img key={i} src={m.url || m.dataUrl} className="aspect-square object-cover rounded" alt=""/>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-brand-soft space-y-2">
            {!ended && booking.meeting_url && <VideoPrejoinPanel meetingUrl={booking.meeting_url}/>}
            {!started && !ended && (
              <button onClick={startConsult} className="btn-primary w-full flex items-center justify-center gap-2 !py-2.5" data-testid="start-consult">
                <Play size={14}/> Start konsultation
              </button>
            )}
            {started && !ended && (
              <>
                <button onClick={endConsult} className="btn-secondary w-full flex items-center justify-center gap-2 !py-2.5" data-testid="end-consult">
                  <Square size={12}/> Afslut konsultation
                </button>
                {user?.role === "expert" && (
                  <button onClick={flagUnsafe} className="w-full text-xs text-[#991B1B] flex items-center justify-center gap-1 pt-1" data-testid="flag-unsafe">
                    <AlertTriangle size={12}/> Marker som usikker at fortsætte
                  </button>
                )}
              </>
            )}
            {ended && user?.role === "customer" && !reviewed && !showReview && (
              <button onClick={() => setShowReview(true)} className="btn-primary w-full !py-2.5">Giv anmeldelse</button>
            )}
            {ended && user?.role === "customer" && reviewed && (
              <div className="rounded-lg bg-brand-soft px-3 py-2 text-xs font-medium text-[#166534]">Tak for din anmeldelse</div>
            )}
            {ended && user?.role === "customer" && expert?.id && (
              <button
                type="button"
                onClick={() => nav(`/eksperter/${expert.id}`)}
                className="btn-secondary w-full !py-2.5"
                data-testid="book-same-expert-again"
              >
                Få hjælp af {expert.name?.split(" ")[0] || "eksperten"} igen
              </button>
            )}
            {ended && (
              <div className="text-xs text-brand-muted flex items-center gap-1.5 pt-1"><PhoneOff size={12}/> Konsultationen er afsluttet.</div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-brand-soft text-[11px] text-brand-muted space-y-0.5">
            <div>Booking <span className="font-mono text-brand-ink">{booking.id.slice(0, 8)}</span></div>
            <div>Pris <span className="font-medium text-brand-ink">{formatDkk(booking.price_dkk)}</span></div>
          </div>
        </aside>

        {/* Chat */}
        <section className="card-outline flex flex-col overflow-hidden consult-chat-shell">
          <div className="px-4 sm:px-5 py-3 border-b border-brand-line flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-display font-semibold text-brand-ink">Chat</span>
              <span className="text-[10px] text-brand-muted">{messages.length} beskeder</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${chatDot}`}/>
              <span className="text-xs text-brand-muted" data-testid="chat-status-label">{chatStatus}</span>
            </div>
          </div>
          <div ref={chatRef} className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-2.5 scrollbar-thin">
            {messages.length === 0 && <div className="text-sm text-brand-muted">Ingen beskeder endnu. Præsenter dig og problemet.</div>}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_id === user?.id ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${m.sender_id === user?.id ? "bg-brand-green text-white" : "bg-brand-soft text-brand-ink"}`}>
                  <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name} · {m.sender_role}</div>
                  {m.content}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-brand-line flex gap-2" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}>
            <input
              value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={ended ? "Konsultationen er afsluttet" : "Skriv en besked…"}
              disabled={ended}
              className="flex-1 px-3.5 py-2.5 card-outline focus:outline-none text-sm disabled:opacity-60 min-h-[44px]"
              data-testid="chat-input"
            />
            <button onClick={send} disabled={ended} className="btn-primary flex items-center gap-1 !px-4 !py-2.5 disabled:opacity-60 min-h-[44px]" data-testid="chat-send">
              <Send size={16}/>
            </button>
          </div>
        </section>
      </div>

      {showReview && (
        <div className="fixed inset-0 z-50 bg-brand-ink/40 flex items-center justify-center p-4">
          <div className="card-outline p-5 sm:p-6 max-w-md w-full" data-testid="review-modal">
            <h3 className="font-display text-xl font-bold text-brand-ink">Hjalp konsultationen?</h3>
            <p className="text-sm text-brand-body mt-1">Din feedback hjælper andre.</p>
            {[["rating", "Samlet"], ["expertise", "Ekspertise"], ["helpfulness", "Hjælpsomhed"], ["communication", "Kommunikation"]].map(([k, l]) => (
              <div key={k} className="mt-3">
                <div className="font-mono uppercase text-[10px] tracking-widest text-brand-muted">{l}</div>
                <div className="mt-1 flex gap-1">
                  {[1,2,3,4,5].map((n) => (
                    <button key={n} onClick={() => setReview((r) => ({ ...r, [k]: n }))} data-testid={`review-${k}-${n}`}>
                      <Star size={20} className={n <= review[k] ? "fill-[#D97706] text-[#D97706]" : "text-brand-line"}/>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <textarea value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })}
              placeholder="Skriv en kort kommentar (valgfrit)"
              className="mt-3 w-full card-outline p-3 text-sm resize-none" rows={3} data-testid="review-comment"/>
            {reviewError && <p className="mt-3 text-sm text-[#991B1B]" role="alert">{reviewError}</p>}
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => setShowReview(false)} disabled={reviewBusy} className="btn-secondary flex-1">Ikke nu</button>
              <button type="button" onClick={submitReview} disabled={reviewBusy} className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="review-submit">
                <Check size={14}/> {reviewBusy ? "Gemmer…" : "Send anmeldelse"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

